import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import { DeviceCommandsPage } from './commands/deviceCommandsPage';
import { DeviceStatusPage } from './status/deviceStatusPage/DeviceStatusPage';
import { DeviceSchedulePage } from './schedule/deviceSchedulePage/DeviceSchedulePage';
import { DeviceEventsPage } from './events/deviceEventsPage';
import { useParams } from 'util/reactRouterHooks';
import { HasUrlSlug, HasDeviceId, getBuildingDevice, getBuildingIdByUrlSlug } from 'selectors/buildingsSelectors';
import { State } from 'reducers';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { useMenu, MenuItem } from 'hooks';

export const BuildingDeviceRouter = () => {
    const params = useParams<HasUrlSlug & HasDeviceId>();
    const { urlSlug, deviceId } = params;

    const buildingId = useSelector((state: State) => getBuildingIdByUrlSlug(state, params));
    const device = useSelector((state: State) => getBuildingDevice(state, params));
    const hasSchedule = device && device.deviceClass === 'display';

    const { hasPermission } = useSession();
    const { pushMenu, popMenu } = useMenu();

    //
    // Create/Update the buildings menu when the BuildingRouter is
    // mounted, the building changes, or the user permissions change
    //
    useEffect(() => {
        const items: MenuItem[] = [];

        items.push({ id: 'device-status-link',   link: `/buildings/${urlSlug}/devices/${deviceId}/status`,   textKey: 'DEVICE.STATUS.LINK' });

        if (hasSchedule) {
            items.push({ id: 'device-schedule-link', link: `/buildings/${urlSlug}/devices/${deviceId}/schedule`, textKey: 'DEVICE.SCHEDULE.LINK' });
        }

        items.push({ id: 'device-events-link',   link: `/buildings/${urlSlug}/devices/${deviceId}/events`,   textKey: 'DEVICE.EVENTS.LINK' });

        if (hasPermission(buildingId, Permission.CommandDevice)) {
            items.push({ id: 'device-commands-link', link: `/buildings/${urlSlug}/devices/${deviceId}/commands`, textKey: 'DEVICE.COMMANDS.LINK' });
        }

        pushMenu({ id: 'menu-device', textKey: 'DEVICE.MENU', items }, 2);

        // Remove the menu when we are unmounted
        return () => popMenu(2);
    }, [ urlSlug, deviceId, hasSchedule, pushMenu, popMenu, hasPermission ]);

    return (
        <Switch>
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/devices/:deviceId/status" component={ DeviceStatusPage }/>
            }
            { hasPermission(buildingId, Permission.EditTheme) &&
                <Route path="/buildings/:urlSlug/devices/:deviceId/schedule" component={ DeviceSchedulePage }/>
            }
            { hasPermission(buildingId, Permission.CommandDevice) &&
                <Route path="/buildings/:urlSlug/devices/:deviceId/commands" component={ DeviceCommandsPage }/>
            }
            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Route path="/buildings/:urlSlug/devices/:deviceId/events" component={ DeviceEventsPage }/>
            }

            { hasPermission(buildingId, Permission.ViewDevices) &&
                <Redirect to={ `/buildings/${urlSlug}/devices/${deviceId}/status` }/>
            }
        </Switch>
    );
};
